import React from 'react';
import { AlertTriangle, Check, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { formatLeave, normUnit } from '../lib/leaveUnits';

/**
 * Who changes if a Location takes this working week — shown BEFORE it is saved.
 *
 * ⚠️ ROB'S OPTION A, 06/08/2026. Step 3 of the locations plan is the first change that
 * moves real numbers for real people, so the consequence is shown at the moment the
 * week is chosen rather than discovered afterwards in somebody's balance.
 *
 * ⚠️ IT NEVER BLOCKS THE SAVE (his decision). Same posture as the statutory rest-break
 * advisory: tell the admin what will happen, don't overrule them about their own
 * business.
 *
 * ⚠️ EVERY FIGURE IS THE EMPLOYEE'S OWN UNIT, via `lib/leaveUnits`. A day is not a
 * fixed quantity — for somebody on hours, moving an office from 7.5-hour to 8-hour days
 * leaves a count of days reading the same while the time it represents has changed.
 * Rob asked exactly this, and it is why the server also sends weekly hours.
 */
const hrs = (mins) => `${(((mins || 0) / 60) * 10 % 10 === 0
  ? (mins || 0) / 60
  : ((mins || 0) / 60).toFixed(1))} hrs`;

const WorkingWeekChangePreview = ({ preview, loading, error, onBack, onConfirm, saving }) => {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
        <Loader2 className="w-4 h-4 animate-spin" />
        Working out what this changes…
      </div>
    );
  }

  // ⚠️ A PREVIEW THAT FAILED MUST NOT LOOK LIKE "NOTHING CHANGES". Saying so and
  // letting them save anyway is honest; a silent empty panel is not.
  if (error) {
    return (
      <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
        <p className="text-sm text-amber-700">
          Couldn't work out what this changes. You can still save — the working week
          will apply as normal.
        </p>
      </div>
    );
  }

  const people = preview?.people || [];

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <p className="text-sm font-medium">What this changes</p>
        <span className="text-xs text-muted-foreground">{preview?.location}</span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Nothing is saved yet. This is what would happen if you save.
      </p>

      {people.length === 0 && (
        <p className="text-sm text-muted-foreground py-3">
          Nobody is assigned to this location yet, so this changes nobody's leave today.
        </p>
      )}

      <div className="border-t border-border">
        {people.map((p) => {
          const unit = normUnit(p.display_unit);
          const changedMinutes = p.leave_change_minutes || 0;
          // ⚠️ Their own unit. `basis` is the day length AFTER the change, because that
          // is the day the new figure is measured in.
          // ⚠️ ABSOLUTE ON BOTH PATHS. `formatLeave` renders the sign of whatever it
          // is handed, so passing a negative AND prefixing a minus gave "−-0.4 days".
          // The hours path used Math.abs and the days path did not — one bug, visible
          // in only one direction.
          const abs = Math.abs(changedMinutes);
          const amount = formatLeave(
            p.day_minutes_then ? abs / p.day_minutes_then : null,
            { unit, minutes: abs, basis: p.day_minutes_then },
          );

          return (
            <div key={p.user_id} className="flex gap-3 items-start py-3 border-b border-border">
              <div className="flex-1 min-w-0">
                <p className="text-sm">{p.name}</p>
                {p.keeps_own_pattern ? (
                  /* ⚠️ NAME THE PATTERN, don't just assert one exists. "Has their own
                     pattern" is unfalsifiable on its own; showing the days lets an
                     admin see at a glance whether it is genuinely personal or an
                     artefact of how that person was set up. */
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Own pattern{p.own_pattern_days?.length
                      ? ` (${p.own_pattern_days.map((d) => d.slice(0, 3)).join(', ')})`
                      : ''} — keeps it
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {p.why || 'No change'}
                  </p>
                )}
              </div>
              <div className="text-right shrink-0">
                {p.keeps_own_pattern || (!p.why && !changedMinutes) ? (
                  <p className="text-xs text-green-600 flex items-center gap-1 justify-end">
                    <Check className="w-3 h-3" /> No change
                  </p>
                ) : (
                  <>
                    <p className="text-xs text-muted-foreground">
                      Works {p.weekly_minutes_now === p.weekly_minutes_then
                        ? hrs(p.weekly_minutes_then)
                        : `${hrs(p.weekly_minutes_now)} → ${hrs(p.weekly_minutes_then)}`} a week
                    </p>
                    {changedMinutes !== 0 && (
                      <p className={`text-xs mt-0.5 ${changedMinutes < 0 ? 'text-amber-600' : 'text-green-600'}`}>
                        {amount} {changedMinutes < 0 ? 'less' : 'more'} leave
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {preview?.changed_count > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 mt-3 flex gap-2 items-start">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700">
            {preview.changed_count} {preview.changed_count === 1 ? 'person' : 'people'} at this
            location will have their leave recalculated.
            {preview.unaffected_count > 0 && ` ${preview.unaffected_count} `}
            {preview.unaffected_count > 0 && (preview.unaffected_count === 1 ? 'is' : 'are')}
            {preview.unaffected_count > 0 && ' unaffected.'}
          </p>
        </div>
      )}

    </div>
  );
};

/**
 * The preview's actions, for the dialog's OWN pinned footer.
 *
 * ⚠️ SEPARATE FROM THE LIST ON PURPOSE. They used to sit at the bottom of the panel,
 * which sat outside the dialog's scrolling area — so a location with fourteen people
 * pushed the buttons off the bottom of the screen with no way to reach them. Rob hit
 * exactly that. The list scrolls; the buttons stay put.
 */
export const WorkingWeekChangePreviewActions = ({ onBack, onConfirm, saving, error }) => (
  <>
    <Button type="button" variant="outline" onClick={onBack} disabled={saving}>
      Back
    </Button>
    <Button type="button" onClick={onConfirm} disabled={saving}>
      {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
      {error ? 'Save anyway' : 'Save this working week'}
    </Button>
  </>
);

export default WorkingWeekChangePreview;
